import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { GET_NOTIFICATION_CONFIGS } from '../graphql/queries';
import {
  CREATE_NOTIFICATION_CONFIG,
  UPDATE_NOTIFICATION_CONFIG,
  DELETE_NOTIFICATION_CONFIG,
} from '../graphql/mutations';

const EVENT_OPTIONS = [
  'build.started',
  'build.completed',
  'build.failed',
  'scan.completed',
  'scan.critical_found',
  'image.deprecated',
  'image.expired',
  'lifecycle.expiring_soon',
];

export default function Notifications() {
  const { data, loading, error, refetch } = useQuery(GET_NOTIFICATION_CONFIGS);
  const [createConfig, { loading: creating }] = useMutation(CREATE_NOTIFICATION_CONFIG);
  const [updateConfig] = useMutation(UPDATE_NOTIFICATION_CONFIG);
  const [deleteConfig] = useMutation(DELETE_NOTIFICATION_CONFIG);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    channel: 'slack',
    webhookUrl: '',
    events: [],
    enabled: true,
  });

  const configs = data?.listNotificationConfigs || [];

  const toggleEvent = (event) => {
    const next = form.events.includes(event)
      ? form.events.filter((e) => e !== event)
      : [...form.events, event];
    setForm({ ...form, events: next });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.webhookUrl || form.events.length === 0) return;
    try {
      await createConfig({
        variables: {
          input: {
            channel: form.channel,
            webhookUrl: form.webhookUrl,
            events: form.events,
            enabled: form.enabled,
          },
        },
      });
      toast.success('Notification channel created');
      setShowForm(false);
      setForm({ channel: 'slack', webhookUrl: '', events: [], enabled: true });
      refetch();
    } catch (err) {
      toast.error(`Failed to create notification config: ${err.message}`);
    }
  };

  const handleToggle = async (config) => {
    try {
      await updateConfig({
        variables: {
          id: config.id,
          input: {
            channel: config.channel,
            webhookUrl: config.webhookUrl,
            events: config.events,
            enabled: !config.enabled,
          },
        },
      });
      toast.success(`Channel ${config.enabled ? 'disabled' : 'enabled'}`);
      refetch();
    } catch (err) {
      toast.error(`Failed to update config: ${err.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification configuration?')) return;
    try {
      await deleteConfig({ variables: { id } });
      toast.success('Notification channel deleted');
      refetch();
    } catch (err) {
      toast.error(`Failed to delete config: ${err.message}`);
    }
  };

  const handleTestNotification = (config) => {
    toast.info(`Test notification sent to ${config.channel} channel`);
  };

  if (loading) return <div className="loading">Loading notification configurations...</div>;
  if (error) return <div className="error">Failed to load notifications: {error.message}</div>;

  return (
    <div>
      <h2 className="page-title">Notification Settings</h2>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3>Notification Channels</h3>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }}
            onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Channel'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleCreate} style={{ marginBottom: 24, padding: 16, background: '#f5f6fa', borderRadius: 8 }}>
            <div className="form-row">
              <div className="form-group">
                <label>Channel</label>
                <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                  <option value="slack">Slack</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="webhook">Generic Webhook</option>
                </select>
              </div>
              <div className="form-group">
                <label>Webhook URL</label>
                <input value={form.webhookUrl} onChange={(e) => setForm({ ...form, webhookUrl: e.target.value })}
                  placeholder="https://hooks.slack.com/services/..." />
              </div>
            </div>
            <div className="form-group">
              <label>Events</label>
              <div className="notification-events-grid">
                {EVENT_OPTIONS.map((event) => (
                  <label key={event} className="notification-event-checkbox">
                    <input type="checkbox" checked={form.events.includes(event)} onChange={() => toggleEvent(event)} />
                    <span>{event}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                Enable immediately
              </label>
            </div>
            <button className="btn btn-primary" type="submit" disabled={creating || !form.webhookUrl || form.events.length === 0}>
              {creating ? 'Creating...' : 'Create Notification Config'}
            </button>
          </form>
        )}

        {configs.length === 0 ? (
          <p>No notification channels configured. Click "Add Channel" to get started.</p>
        ) : (
          <div className="notification-cards">
            {configs.map((config) => (
              <div key={config.id} className={`notification-card ${!config.enabled ? 'notification-card-disabled' : ''}`}>
                <div className="notification-card-header">
                  <div>
                    <strong style={{ fontSize: 16 }}>
                      {config.channel === 'slack' ? 'Slack' : config.channel === 'teams' ? 'Microsoft Teams' : 'Webhook'}
                    </strong>
                    <span className={`badge badge-${config.enabled ? 'success' : 'error'}`} style={{ marginLeft: 8 }}>
                      {config.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <div className="actions" style={{ marginTop: 0 }}>
                    <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }}
                      onClick={() => handleToggle(config)}>
                      {config.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button className="btn" style={{ padding: '4px 12px', fontSize: 12, background: '#00b894', color: '#fff' }}
                      onClick={() => handleTestNotification(config)}>
                      Test
                    </button>
                    <button className="btn btn-danger" style={{ padding: '4px 12px', fontSize: 12 }}
                      onClick={() => handleDelete(config.id)}>
                      Delete
                    </button>
                  </div>
                </div>
                <div className="notification-card-body">
                  <div className="detail-item">
                    <label>Webhook URL</label>
                    <p style={{ fontSize: 13, wordBreak: 'break-all' }}>{config.webhookUrl}</p>
                  </div>
                  <div className="detail-item" style={{ marginTop: 8 }}>
                    <label>Events</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {config.events?.map((event) => (
                        <span key={event} className="badge badge-info">{event}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
